import { useState, useCallback, useRef } from 'react';
import { BenchmarkCard } from './BenchmarkCard';
import { CarouselControls } from './CarouselControls';
import type { BenchmarkCarouselProps } from './BenchmarkCarousel.types';
import { CAROUSEL_DIMENSIONS } from './benchmarkCarousel.constants';
import { eases } from '@/shared/constants/motion.constants';
import { MotionBox } from '@/shared/ui/motion';
import { getWidgetStores } from '@/entities/widget';
import { useTranslation } from 'react-i18next';

// Number of clones on each side for smooth infinite scrolling
const CLONE_COUNT = 2;

export const BenchmarkCarousel = ({ references, exporting = false }: BenchmarkCarouselProps) => {
  const { t } = useTranslation('contentTest');

  const totalCount = references.length;
  // Start at CLONE_COUNT (first real item after clones)
  const [currentIndex, setCurrentIndex] = useState(CLONE_COUNT);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [skipAnimation, setSkipAnimation] = useState(false);
  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  // Track if we need to snap back after animation completes
  const pendingSnapRef = useRef<number | null>(null);

  // Create infinite loop array for carousel
  // Clone last N items, then all real items, then clone first N items
  const infiniteReferences =
    totalCount <= 1
      ? references
      : [
          ...references.slice(-CLONE_COUNT), // Last N items cloned at start
          ...references, // All real items
          ...references.slice(0, CLONE_COUNT), // First N items cloned at end
        ];

  // Determine whether to show controls
  const showPeekAndControls = totalCount > 1;

  // Calculate the snap-back index if we're in clone territory
  const getSnapBackIndex = useCallback(
    (index: number): number | null => {
      if (totalCount <= 1) return null;

      const lastRealIndex = CLONE_COUNT + totalCount - 1;
      const firstRealIndex = CLONE_COUNT;

      if (index < firstRealIndex) {
        // We're in the leading clones, jump to the end of real items
        return lastRealIndex - (firstRealIndex - index - 1);
      } else if (index > lastRealIndex) {
        // We're in the trailing clones, jump to the start of real items
        return firstRealIndex + (index - lastRealIndex - 1);
      }
      return null;
    },
    [totalCount],
  );

  const handleAnimationComplete = useCallback(() => {
    const snapIndex = pendingSnapRef.current;
    if (snapIndex !== null) {
      setSkipAnimation(true);
      setCurrentIndex(snapIndex);
      pendingSnapRef.current = null;

      // Re-enable animation after the jump (next frame)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSkipAnimation(false);
          setIsTransitioning(false);
        });
      });
    } else {
      setIsTransitioning(false);
    }
  }, []);

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;

    const newIndex = currentIndex - 1;
    const snapBack = getSnapBackIndex(newIndex);

    if (snapBack !== null) {
      pendingSnapRef.current = snapBack;
    }

    setIsTransitioning(true);
    setCurrentIndex(newIndex);
  }, [isTransitioning, currentIndex, getSnapBackIndex]);

  const handleNext = useCallback(() => {
    if (isTransitioning) return;

    const newIndex = currentIndex + 1;
    const snapBack = getSnapBackIndex(newIndex);

    if (snapBack !== null) {
      pendingSnapRef.current = snapBack;
    }

    setIsTransitioning(true);
    setCurrentIndex(newIndex);
  }, [isTransitioning, currentIndex, getSnapBackIndex]);

  const handleDotClick = useCallback(
    (dotIndex: number) => {
      if (isTransitioning) return;
      // Convert dot index to carousel index - no snap needed for direct dot clicks
      pendingSnapRef.current = null;
      setIsTransitioning(true);
      setCurrentIndex(CLONE_COUNT + dotIndex);
    },
    [isTransitioning],
  );

  // Don't render if no references
  if (references.length === 0) {
    return null;
  }

  // PDF export variation for carousel
  if (exporting) {
    return (
      <div className="flex w-full flex-col gap-12 bg-surface px-21 pt-20 pb-16">
        <p className="w-full text-display-sm text-foreground">
          {t('ideationWidget.benchmarkCarousel.getInspiredByBenchmarkReferences')}
        </p>
        {references.map((reference, index) => (
          <BenchmarkCard key={`benchmark-export-${index}`} reference={reference} exporting />
        ))}
      </div>
    );
  }

  const cardWidthVw = isFullscreen ? (1087 / 1440) * 100 : ((730 * 1000) / 900 / 1440) * 100;
  const cardGap = isFullscreen ? CAROUSEL_DIMENSIONS.cardGap : 64;
  // When there's only 1 item, no clones exist so index should be 0
  const effectiveIndex = totalCount <= 1 ? 0 : currentIndex;
  const translateX = `calc(-${effectiveIndex * cardWidthVw}vw - ${effectiveIndex * cardGap}px)`;
  const currentDotIndex = (((currentIndex - CLONE_COUNT) % totalCount) + totalCount) % totalCount;

  return (
    <div className="relative flex w-full flex-col items-center gap-12 overflow-x-hidden bg-surface pt-20 pb-16">
      {infiniteReferences.length > 0 && (
        <>
          <div className="flex h-18 w-full flex-col items-center justify-center pb-6 pl-14.5">
            <p className="w-full text-display-sm text-foreground">
              {t('ideationWidget.benchmarkCarousel.getInspiredByBenchmarkReferences')}
            </p>
          </div>

          {/* Opaque masks, not gradients: they cover the cards that overflow the
              gutter on each side so a neighbour never peeks past the padding. */}
          <div
            className={`relative flex w-full items-stretch self-start px-21 before:pointer-events-none before:absolute before:left-0 before:z-1 before:h-full before:w-21 before:bg-surface before:content-[''] ${
              showPeekAndControls
                ? "after:pointer-events-none after:absolute after:top-0 after:right-0 after:bottom-0 after:z-1 after:h-full after:w-21 after:bg-surface after:content-['']"
                : ''
            }`}
            style={{ gap: `${cardGap}px` }}
          >
            <MotionBox
              animate={{ translateX }}
              transition={skipAnimation ? { duration: 0 } : { duration: 0.66, ease: eases.expoOut }}
              onAnimationComplete={handleAnimationComplete}
              className="flex"
              style={{ gap: `${cardGap}px` }}
            >
              {infiniteReferences.map((reference, index) => (
                <div
                  key={`carousel-card-${index}`}
                  className="flex shrink-0"
                  style={{ width: `${cardWidthVw}vw` }}
                >
                  <BenchmarkCard
                    reference={reference}
                    isTransitioning={isTransitioning}
                    isSelected={index === effectiveIndex}
                    isNext={index === effectiveIndex + 1}
                  />
                </div>
              ))}
            </MotionBox>
          </div>
        </>
      )}

      {/* Carousel Controls */}
      {showPeekAndControls && (
        <CarouselControls
          currentIndex={currentDotIndex}
          totalCount={totalCount}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onDotClick={handleDotClick}
        />
      )}
    </div>
  );
};
